import React, { useEffect, useState } from 'react';
import {
  IdentificationCard, Lock, PencilSimple, ArrowRight, Info, ArrowClockwise, Check, X, ShieldCheck,
} from '@phosphor-icons/react';
import SearchableSelect from './SearchableSelect';
import { getPANList, getContactDocumentDetails, updatePanNumber, saveData } from '../api/connectionRegistryApi';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import SectionLock from './SectionLock';

const panPattern = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;

function PanChangeForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [panList, setPanList] = useState([]);
  const [panNo, setPanNo] = useState('');
  const [contactDetails, setContactDetails] = useState([]);
  const [newPanNo, setNewPanNo] = useState('');
  const [reason, setReason] = useState('');

  const [loadingDetails, setLoadingDetails] = useState(false);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingOldPan, setPendingOldPan] = useState('');
  const [pendingNewPan, setPendingNewPan] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  const record = contactDetails[0] || null;

  useEffect(() => {
    getPANList()
      .then((res) => setPanList(Array.isArray(res) ? res : []))
      .catch(() => {
        setPanList([]);
        showToast('error', 'Load Failed', 'Failed to load PAN numbers. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const handlePanChange = async (value) => {
    setPanNo(value || '');
    setContactDetails([]);
    setNewPanNo('');
    setReason('');
    setTouched(false);
    if (!value) return;

    setLoadingDetails(true);
    try {
      const rows = await getContactDocumentDetails(value);
      const list = Array.isArray(rows) ? rows : [];
      setContactDetails(list);
      if (list.length === 0) {
        showToast('warn', 'No Data Found', 'No contact/document details found for the selected PAN.');
      }
    } catch (err) {
      console.error('[getContactDocumentDetails] error =', err);
      setContactDetails([]);
      showToast('error', 'Load Failed', apiErrorMessage(err, 'Could not load contact details for this PAN.'));
    } finally {
      setLoadingDetails(false);
    }
  };

  const samePan = !!newPanNo && !!panNo && newPanNo.toUpperCase() === panNo.toUpperCase();
  const panExists = !!newPanNo && panList.some((p) => (p.pan_number || '').toUpperCase() === newPanNo.toUpperCase());
  const panInvalid = touched && !panNo;
  const newPanFormatInvalid = touched && !panPattern.test(newPanNo);
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!panNo && panPattern.test(newPanNo) && !samePan && !panExists
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);

    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    if (contactDetails.length === 0) {
      showToast('warn', 'No Data Found', 'No contact/document details found for the selected PAN. Cannot proceed.');
      return;
    }

    setPendingOldPan(panNo);
    setPendingNewPan(newPanNo.toUpperCase());
    setPendingReason(reason.trim());
    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const confirmSave = async () => {
    setSaving(true);

    const newPan = newPanNo.toUpperCase();
    const trimmedReason = reason.trim();
    const branchschema = record?.branch_code ?? '';
    const contactRefId = record?.contact_reference_id ?? '';
    const contactId = record?.tbl_mst_contact_id ?? 0;
    // A LEFT JOIN can yield rows with no document id at all; those would land in
    // the SQL IN(...) list as an empty slot and break the whole update.
    const documentIds = [...new Set(
      contactDetails.map((d) => d.tbl_mst_contact_documents_id).filter((id) => id !== null && id !== undefined && id !== ''),
    )].join(',');

    const oldData = contactDetails.map((d) => ({
      contact_reference_id: d.contact_reference_id,
      contact_name: d.contact_name,
      pan_number: panNo,
      document_reference_no: d.document_reference_no,
      tbl_mst_contact_documents_id: d.tbl_mst_contact_documents_id,
      branch_code: d.branch_code,
      tbl_mst_contact_id: d.tbl_mst_contact_id,
    }));

    try {
      const res = await updatePanNumber({
        branchschema,
        oldPanNumber: panNo,
        newPanNumber: newPan,
        contactReferenceId: contactRefId,
        contactId,
        documentIds,
      });

      // UpdatePanNumber rolls back and returns the exception text with HTTP 200,
      // so the response body is the only reliable success signal.
      const text = typeof res === 'string' ? res : JSON.stringify(res ?? '');
      if (!text.toLowerCase().includes('success')) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('UpdatePanNumber reported a failure:', text);
        showToast('error', 'Update Failed', text || 'Update failed. Please try again.', true);
        return;
      }

      const newData = contactDetails.map((d) => ({
        contact_reference_id: d.contact_reference_id,
        contact_name: d.contact_name,
        pan_number: newPan,
        document_reference_no: newPan,
        tbl_mst_contact_documents_id: d.tbl_mst_contact_documents_id,
        branch_code: d.branch_code,
        tbl_mst_contact_id: d.tbl_mst_contact_id,
      }));

      try {
        await saveData([{
          schemaName: branchschema,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: 'PAN_CHANGE',
          oldData,
          newData,
          reason: trimmedReason,
        }]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', text, true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('[saveData audit] error =', auditErr);
        showToast('warn', 'Partial Success', 'PAN updated but audit log failed. Please check logs.', true);
        handleClear();
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('PAN change failed:', err);
      showToast('error', 'PAN Change Failed', apiErrorMessage(err, 'PAN change failed. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setPanNo('');
    setContactDetails([]);
    setNewPanNo('');
    setReason('');
    setTouched(false);
    setShowConfirmModal(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="pan-change-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="pan-change-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <IdentificationCard size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Change Request</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>PAN Change</div>
          </div>
        </div>

        {/* SECTION A: PAN details */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>PAN Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Pick the existing PAN, then enter the corrected one</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: '16px', alignItems: 'start' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '8px' }}>
              <Lock size={13} /> Existing PAN
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>READ ONLY</span>
            </div>

            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>PAN No. <span style={{ color: '#DC2626' }}>*</span></label>
            <SearchableSelect
              key={`pan-${formKey}`}
              options={panList}
              getLabel={(o) => o.pan_number}
              getValue={(o) => o.pan_number}
              value={panNo}
              onChange={handlePanChange}
              placeholder="Select PAN"
              emptyMessage="No PAN numbers found"
              error={panInvalid}
              testId="pan-change-panno"
            />
            {panInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>PAN No. is required</div>}

            <label style={{ display: 'block', fontSize: '11px', marginTop: '10px', marginBottom: '2px' }}>Subscriber Name</label>
            <input
              className="input-field"
              style={{ fontSize: '13px', background: '#F9FAFB' }}
              value={record?.contact_name || ''}
              readOnly
              placeholder="—"
              data-testid="pan-change-subscriber-name"
            />
            {loadingDetails && <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>Loading contact details...</div>}
            {!loadingDetails && panNo && contactDetails.length > 0 && (
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '4px' }}>
                {contactDetails.length} document record(s) linked to this PAN
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '40px', color: '#9CA3AF' }}>
            <ArrowRight size={18} />
            <span style={{ fontSize: '10px', marginTop: '2px' }}>Update</span>
          </div>

          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#002FA7', marginBottom: '8px' }}>
              <PencilSimple size={13} /> New PAN
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>EDITABLE</span>
            </div>

            <label style={{ display: 'block', fontSize: '11px', marginBottom: '2px' }}>New PAN Number <span style={{ color: '#DC2626' }}>*</span></label>
            <input
              className="input-field"
              style={{
                fontSize: '13px',
                letterSpacing: '0.06em',
                borderColor: (newPanFormatInvalid || (touched && (samePan || panExists))) ? '#DC2626' : undefined,
              }}
              value={newPanNo}
              maxLength={10}
              disabled={!record}
              placeholder="e.g. ABCDE1234F"
              // The Angular field uppercases and strips anything that is not A-Z/0-9 as you type.
              onChange={(e) => setNewPanNo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10))}
              data-testid="pan-change-new-panno"
            />
            {newPanFormatInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Invalid PAN format (e.g. ABCDE1234F)</div>}
            {touched && !newPanFormatInvalid && samePan && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Old and New PAN Numbers cannot be the same</div>}
            {touched && !newPanFormatInvalid && !samePan && panExists && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>This PAN Number already exists in the system</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        <SectionLock locked={!record || !panPattern.test(newPanNo)} hint="Select a PAN and enter a valid new PAN in section A first">
        {/* SECTION B: Reason */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Reason for Change</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Minimum 10 characters — this will be recorded in the audit log</div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.trim().length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter reason (min 10 characters)..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="pan-change-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Reason is required' : 'Reason must be at least 10 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="pan-change-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving || !record} data-testid="pan-change-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '440px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="pan-change-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm PAN Change</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to change the PAN for <strong>{record?.contact_name || 'this subscriber'}</strong> across{' '}
                <strong>{contactDetails.length}</strong> record(s). Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>From</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0, letterSpacing: '0.06em' }}>{pendingOldPan}</p>
                </div>
                <ArrowRight size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>To</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0, letterSpacing: '0.06em' }}>{pendingNewPan}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="pan-change-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="pan-change-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PanChangeForm;
